import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectLccaTriggerData, selectLccaTriggerTotal } from '../store/common.selector';
import { CatalogLccaTriggerResponse } from '../../shared/models/common/lcca-trigger.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'lcca-trigger-list',
  templateUrl: './lcca-trigger-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./lcca-trigger-list.component.scss']
})
export class LccaTriggerListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  lccaTriggers = toSignal(this.store$.select(selectLccaTriggerData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectLccaTriggerTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find LCCA Triggers')), {initialValue: false});

  
  readonly auditResourceName = 'Lcca Trigger';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedLccaTriggers = signal<CatalogLccaTriggerResponse[]>([]);

  // Add LCCA Trigger Form Variables
  dialogVisible = signal(false);
  lccaTriggerName = signal('');
  savingLccaTrigger = signal(false);
  isEditMode = signal(false);
  editingLccaTriggerId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedLccaTriggers().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createLccaTriggerSuccess, action.updateLccaTriggerSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaTrigger.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Delete Success Alert
    this.actions$.pipe(
      ofType(action.deleteLccaTriggerSuccess, action.bulkDeleteLccaTriggersSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedLccaTriggers.set([]);
      this.loadLccaTriggers(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createLccaTriggerError, action.updateLccaTriggerError, action.deleteLccaTriggerError, action.bulkDeleteLccaTriggersError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingLccaTrigger.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findLccaTriggers({
      page,
      size,
      keywords
    }));
  }

  loadLccaTriggers(): void {
    this.store$.dispatch(action.findLccaTriggers({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.lccaTriggerName.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingLccaTriggerId.set(null);
  }

  closeDialog(): void {
    this.lccaTriggerName.set('');
    this.savingLccaTrigger.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingLccaTriggerId.set(null);
  }

  saveLccaTrigger(): void {
    if(!this.lccaTriggerName().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'LCCA trigger name is required'
      });
      return;
    }

    this.savingLccaTrigger.set(true);

    const lccaTriggerData = {
      name: this.lccaTriggerName().trim()
    };

    if(this.isEditMode() && this.editingLccaTriggerId()) {
      this.store$.dispatch(action.updateLccaTrigger({ id: this.editingLccaTriggerId()!, lccaTrigger: lccaTriggerData }))
    } else {
      this.store$.dispatch(action.createLccaTrigger({ lccaTrigger: lccaTriggerData }))
    }
  }

  editLccaTrigger(): void {
    if (this.selectedLccaTriggers().length === 1) {
      const lccaTriggerToEdit = this.selectedLccaTriggers()[0];
      this.lccaTriggerName.set(lccaTriggerToEdit.name ? lccaTriggerToEdit.name : '');
      this.isEditMode.set(true);
      this.editingLccaTriggerId.set(lccaTriggerToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one LCCA trigger to edit'
      });
    }
  }

  deleteLccaTrigger(): void {
    if (this.selectedLccaTriggers().length === 0) {
      return;
    }

    const lccaTriggersToDelete = [...this.selectedLccaTriggers()];
    const count = lccaTriggersToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${lccaTriggersToDelete[0].name}"?`;
    } else {
      const maxDisplay = 10;
      const namesToShow = lccaTriggersToDelete.slice(0, maxDisplay);
      const namesList = namesToShow.map((t, index) => `${index + 1}. ${t.name}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} LCCA triggers?<br><br><strong>Names to be deleted:</strong><br>${namesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (count === 1) {
          this.store$.dispatch(action.deleteLccaTrigger({ id: lccaTriggersToDelete[0].id }));
        } else {
          const ids = lccaTriggersToDelete.map(t => t.id);
          this.store$.dispatch(action.bulkDeleteLccaTriggers({ ids }));
        }
      }
    });
  }

  refreshLccaTriggers(): void {
    this.selectedLccaTriggers.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'LCCA triggers list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedLccaTriggers();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.name || item?.trigger_name || item?.code || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}

